import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Link, Typography, Tooltip, Skeleton, Chip } from "@mui/material";
import { Icon } from "@iconify/react";
import { getDueDateStyles } from "../../../Utils/critiqueDueDateUtils";
import CritiqueStatusChip from "../../../pages/critique/components/CritiqueStatusChip";
import PlanTypeChip from "../../../pages/critique/components/PlanTypeChip";

/**
 * Header content for the Critique Details (Edit Critique) page.
 * Shows: "Critique templates" link (→ all critiques), critique name, and due date or status chip.
 * Due date styling matches AllCritiquesCard (overdue, reaching soon, enough time).
 * Status chip (Completed/OVERDUE_SUBMITTED) matches AllCritiquesCard.
 * Right-side actions (watchers, export, Save, Submit) are rendered by Header.js.
 */
const CritiqueDetailsHeader = ({ critiqueData, loading = false, showReadOnlyLabel = false }) => {
  const navigate = useNavigate();
  const critiqueName = critiqueData?.critique_name || critiqueData?.title || "Untitled Critique";
  const dueDateLabel = critiqueData?.due_date_label || critiqueData?.dueDateLabel || "";
  const dueDate = critiqueData?.due_date || critiqueData?.dueDate || null;
  const status = critiqueData?.status || "due";
  const isCompleted = critiqueData?.is_completed === true;
  const planType = critiqueData?.plan_type || critiqueData?.planType || null;

  const handleBackToAllCritiques = () => {
    navigate("/critique/all");
  };

  const dueDateStyles = getDueDateStyles(dueDate);

  const renderStatusOrDueDate = () => {
    if (isCompleted) {
      return <CritiqueStatusChip status={status} />;
    }
    if (dueDateLabel) {
      return (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            height: 24,
            px: "6px",
            borderRadius: "6px",
            fontWeight: 700,
            fontSize: "12px",
            lineHeight: "20px",
            color: dueDateStyles.color,
            bgcolor: dueDateStyles.backgroundColor,
          }}
        >
          <Icon icon="solar:alarm-bold-duotone" width={16} height={16} style={{ color: dueDateStyles.color }} />
          {dueDateLabel}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" gap="2px" flexDirection="column">
        <Box sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}>
          <Skeleton variant="text" width={120} height={22} sx={{ borderRadius: "4px" }} />
        </Box>
        <Box display="flex" flexDirection="row" alignItems="center" gap={1} sx={{ minHeight: 36 }}>
          <Skeleton variant="rounded" width={280} height={36} sx={{ borderRadius: "4px" }} />
          <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: "6px" }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" gap="2px" flexDirection="column">
      {/* Breadcrumb: Critique templates */}
      <Box sx={{ "& .MuiTypography-root": { fontSize: "14px" } }}>
        <Link
          underline="hover"
          color="inherit"
          component="button"
          onClick={handleBackToAllCritiques}
          className="!text-sm !leading-[22px] !text-[#637381]"
          sx={{ cursor: "pointer", background: "none", border: "none", padding: 0, font: "inherit" }}
        >
          All critiques
        </Link>
      </Box>

      {/* Critique name and due date / status chip row */}
      <Box display="flex" flexDirection="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ minHeight: 24 }}>
        {(() => {
          const fullName = critiqueName || "Untitled Critique";
          const maxLength = 40;
          const isTruncated = fullName.length > maxLength;
          const displayName = isTruncated ? fullName.substring(0, maxLength) + "..." : fullName;
          const typographyContent = (
            <Typography
              component="h1"
              sx={{
                fontSize: "24px",
                lineHeight: "36px",
                fontWeight: 700,
                color: "#212B36",
                flexShrink: 0,
              }}
            >
              {displayName}
            </Typography>
          );
          return isTruncated ? (
            <Tooltip
              title={fullName}
              arrow
              placement="top"
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                  },
                },
              }}
            >
              {typographyContent}
            </Tooltip>
          ) : (
            typographyContent
          );
        })()}
        {renderStatusOrDueDate()}
        <PlanTypeChip planType={planType} />
        {showReadOnlyLabel && (
          <Chip
            size="small"
            label="Read only"
            sx={{
              backgroundColor: "#919EAB29",
              color: "#637381",
              borderRadius: "6px",
              fontWeight: 700,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default CritiqueDetailsHeader;
